import type {
  AdminV2ParameterCalculatorResult,
  AdminV2ParameterCalculatorScalar,
} from '@/admin-v2/tools/parameter-calculator';
import type {
  WorkspaceParameterCalculatorView,
  WorkspaceToolParamValue,
  WorkspaceToolParamView,
} from '../contracts/tool-view';

function isValidTargetValue(
  field: WorkspaceToolParamView,
  value: AdminV2ParameterCalculatorScalar
) {
  if (field.type === 'number' || field.type === 'slider') {
    return (
      typeof value === 'number' &&
      Number.isFinite(value) &&
      (field.min === undefined || value >= field.min) &&
      (field.max === undefined || value <= field.max)
    );
  }
  if (field.type === 'switch') return typeof value === 'boolean';
  if (field.type === 'text' || field.type === 'url') {
    return typeof value === 'string';
  }
  if (field.type === 'select' || field.type === 'segmented-slider') {
    return Boolean(
      field.options?.some((option) => Object.is(option.value, value))
    );
  }
  return false;
}

export function buildWorkspaceParameterCalculatorRecommendations(params: {
  calculator: WorkspaceParameterCalculatorView;
  fields: WorkspaceToolParamView[];
  currentValues: Record<string, WorkspaceToolParamValue>;
  result: AdminV2ParameterCalculatorResult;
}):
  | {
      ok: true;
      noChange: boolean;
      recommendations: Record<string, WorkspaceToolParamValue>;
    }
  | { ok: false } {
  if (params.result.status !== 'ready' && params.result.status !== 'warning') {
    return { ok: false };
  }
  const fieldById = new Map(params.fields.map((field) => [field.id, field]));
  const recommendations: Record<string, WorkspaceToolParamValue> = {};
  for (const mapping of params.calculator.outputMappings) {
    const field = fieldById.get(mapping.targetFieldId);
    const outputValue = params.result.outputs[mapping.outputId];
    const valueMapItem = mapping.valueMap?.find((item) =>
      Object.is(item.from, outputValue)
    );
    const value = mapping.valueMap ? valueMapItem?.to : outputValue;
    if (!field || value === undefined || !isValidTargetValue(field, value)) {
      return { ok: false };
    }
    recommendations[field.id] = value;
  }
  return {
    ok: true,
    noChange: Object.entries(recommendations).every(([fieldId, value]) =>
      Object.is(params.currentValues[fieldId], value)
    ),
    recommendations,
  };
}

export function applyWorkspaceParameterCalculatorRecommendations(params: {
  recommendations: Record<string, WorkspaceToolParamValue>;
  validate: (fieldId: string, value: WorkspaceToolParamValue) => boolean;
  commit: (recommendations: Record<string, WorkspaceToolParamValue>) => void;
}) {
  for (const [fieldId, value] of Object.entries(params.recommendations)) {
    if (!params.validate(fieldId, value)) return false;
  }
  params.commit(params.recommendations);
  return true;
}
